import React, { useCallback, useContext, useRef, useState } from 'react';
import { CSSObject, getFontWeight, Theme } from '@core-ui/react-stitch';
import { transitions } from '@core-ui/motion';
// @ts-ignore
import { RailContext } from '@cat/rail';
import {
  AccordionContext,
  Panel,
  PanelDetails,
  PanelToggle
} from '@core-ui/accordion';
import {
  BreakpointContext,
  MEDIUM,
  XLARGE
} from '@core-ui/breakpoint-provider';
import { FeatureFlagsContext } from '@core-ui/feature-flags';
import { BrandInfoContext } from '@core-ui/brand-info-provider';
import { Localize, useLocalize } from '@core-ui/localization-provider';
import getTotalAmountOfAppliedOptions from '../collaborators/get-total-amount-of-applied-options';
import getAppliedOptionsAsText from '../collaborators/get-applied-options-as-text';
import Facet from '../../Facet';
import {
  FACET_REFERENCE_FACET_LABEL_COLOR,
  FACET_REFERENCE_FACET_LABEL_DEPARTMENT,
  FACET_REFERENCE_FACET_LABEL_PRICE,
  FACET_REFERENCE_FACET_LABEL_SIZE,
  FACET_REFERENCE_FACET_LABEL_STYLE,
  FACET_REFERENCE_FACET_LABEL_RATING
} from '../translations';
import { Facets, Option } from '../../types';

type AccordionItemProps = {
  appliedFacets: { [parameter: string]: Option[] };
  facet: Facets;
  isMobile: boolean;
  scrollableElement: HTMLElement;
  isBRRedesignEnabled?: boolean;
  isSwitchContractEnabled?: boolean;
};

const getFacetSearchOptionListOptions = (options: any): Option[] => {
  if (options !== undefined && Array.isArray(options)) {
    return options;
  }

  return [];
};

const getSearchFacetOptions = (facet: Facets): Option[] => {
  if (Array.isArray(facet.searchFacetOptionGroupList)) {
    return facet.searchFacetOptionGroupList;
  }

  const options = facet.searchFacetOptionGroupList?.searchFacetOptionList;

  return getFacetSearchOptionListOptions(options);
};

const getOptions = (facet: Facets): Option[] => {
  if (Array.isArray(facet.options)) {
    return facet.options;
  }

  return getSearchFacetOptions(facet);
};

const getName = (facet: Facets) => facet.name ?? facet.searchFacetId ?? '';

const renderAppliedCount = (appliedCount: number) =>
  appliedCount > 0 ? ` (${appliedCount})` : null;

const getAppliedCount = (
  isMobile: boolean,
  appliedFacets: { [parameter: string]: Option[] },
  facetName: string
) => (isMobile ? getTotalAmountOfAppliedOptions(appliedFacets, facetName) : 0);

const renderAppliedOptions = (
  isMobile: boolean,
  isOpen: boolean | undefined,
  appliedCount: number,
  appliedOptionsStyle: (theme: Theme) => CSSObject,
  appliedFacets: { [parameter: string]: Option[] },
  facetName: string,
  localize: Localize
) => {
  const isValid = () => isMobile && !isOpen && appliedCount > 0;

  if (isValid()) {
    return (
      <div css={appliedOptionsStyle}>
        {getAppliedOptionsAsText(appliedFacets, facetName, localize)}
      </div>
    );
  }

  return null;
};

const transitionTimeout =
  parseFloat(transitions.expressive.in.transitionDuration) +
  parseFloat(transitions.userTriggeredDelay);

const facetLocalizationKeys: { [key: string]: string } = {
  department: FACET_REFERENCE_FACET_LABEL_DEPARTMENT,
  style: FACET_REFERENCE_FACET_LABEL_STYLE,
  color: FACET_REFERENCE_FACET_LABEL_COLOR,
  price: FACET_REFERENCE_FACET_LABEL_PRICE,
  size: FACET_REFERENCE_FACET_LABEL_SIZE,
  reviewScore: FACET_REFERENCE_FACET_LABEL_RATING
};

const getLocalizedFacetDisplayName = ({
  localize,
  name,
  displayName,
  dynamicFacetName
}: {
  localize: Localize;
  name: string;
  displayName: string;
  dynamicFacetName?: boolean;
}): string => {
  const facetLocalizationKey = facetLocalizationKeys[name];

  return facetLocalizationKey && !dynamicFacetName
    ? localize(facetLocalizationKey)
    : displayName;
};

const getPanelCss = (
  theme: Theme,
  isTablet: boolean,
  isBRRedesignEnabled?: boolean
) => ({
  ...(isBRRedesignEnabled
    ? {
        padding: '0 1rem',
        backgroundColor: theme.color.inverse.b1
      }
    : isTablet && {
        padding: '0 1rem'
      })
});

const AccordionItem = ({
  facet,
  appliedFacets,
  isMobile,
  scrollableElement,
  isBRRedesignEnabled,
  isSwitchContractEnabled
}: AccordionItemProps) => {
  const { localize } = useLocalize();
  const { expanded } = useContext(AccordionContext);
  const { stickyBuffer } = useContext(RailContext);
  const { greaterOrEqualTo, smallerThan } = useContext(BreakpointContext);
  const { enabledFeatures } = useContext(FeatureFlagsContext);
  const { abbrBrand } = useContext(BrandInfoContext);
  const [, updateState] = useState({});
  const timeout = useRef(transitionTimeout);
  const forceUpdate = useCallback(() => {
    setTimeout(() => {
      updateState({});
      timeout.current = 0;
    }, timeout.current);
  }, []);

  // const name: string = facet.name ?? facet.searchFacetId ?? '';
  const name: string = getName(facet);
  const options = getOptions(facet);
  const isOpen = expanded?.includes(facet.name);
  const isBrRedesignFont =
    enabledFeatures?.[`enable-new-${abbrBrand.replace('fs', '')}-fonts`];
  const isMobileOrBRRedesign = isMobile || !!isBRRedesignEnabled;

  // eslint-disable-next-line no-param-reassign
  facet = {
    ...facet,
    name,
    options,
    displayName: getLocalizedFacetDisplayName({
      localize,
      name,
      displayName: facet.displayName,
      dynamicFacetName: facet.dynamicFacetName
    })
  };

  const appliedCount = getAppliedCount(
    isMobileOrBRRedesign,
    appliedFacets,
    facet.name
  );

  const appliedOptionsStyle = (theme: Theme): CSSObject => ({
    ...theme.brandFont,
    color: theme.color.g1,
    fontWeight: 'normal',
    fontSize: '0.75rem',
    overflow: 'hidden',
    paddingTop: '0.25rem',
    textTransform: 'capitalize',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    letterSpacing: 'normal',
    ...(isBRRedesignEnabled && {
      color: theme.color.b2
    })
  });

  const chevronSVG =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1.5 L 5.5 5.5 L 10 1.5' fill='none' stroke='%232c2824' stroke-width='2'%3E%3C/path%3E%3C/svg%3E\")";

  const panelToggleStyle = (theme: Theme) => ({
    zIndex: 101,
    top: `${stickyBuffer}px`,
    ...(isBrRedesignFont ? getFontWeight('regular') : {}),
    ...(isBRRedesignEnabled && {
      backgroundColor: theme.color.inverse.b1,
      color: theme.color.b2,
      padding: '1rem 0',
      letterSpacing: '0.063rem',
      '&::before, &::after': {
        right: '0.2rem'
      },
      '&::after': {
        backgroundImage: chevronSVG
      }
    })
  });

  const isTablet = greaterOrEqualTo(MEDIUM) && smallerThan(XLARGE);

  return (
    <Panel
      key={facet.name}
      css={theme => getPanelCss(theme, isTablet, isBRRedesignEnabled)}
      id={facet.name}
      scrollableElement={scrollableElement}
    >
      <PanelToggle css={panelToggleStyle}>
        {facet.displayName}
        {renderAppliedCount(appliedCount)}
        {renderAppliedOptions(
          isMobileOrBRRedesign,
          isOpen,
          appliedCount,
          appliedOptionsStyle,
          appliedFacets,
          facet.name,
          localize
        )}
      </PanelToggle>
      <PanelDetails>
        <Facet
          appliedFacets={appliedFacets}
          facet={facet}
          isBRRedesignEnabled={isBRRedesignEnabled}
          isMobile={isMobile}
          isSwitchContractEnabled={isSwitchContractEnabled}
          onChange={forceUpdate}
        />
      </PanelDetails>
    </Panel>
  );
};

export default AccordionItem;
